import { useReduxState } from 'hooks';
import { FormGroup } from 'components/common/form';
import { SelectInput } from 'components/common/form/common';

interface Props {
  blockId: number;
  disabled?: boolean;
  maxNumberOfOptions: number;
  numberOfAllowedSelectionsMax?: number;
  numberOfAllowedSelectionsMin?: number;
  onMaximumSelectionsChange: (value: number) => void;
  onMinimumSelectionsChange: (value: number) => void;
}

const EditorBlockChoiceTasksSelectionSettings = ({
  blockId,
  disabled = false,
  maxNumberOfOptions,
  numberOfAllowedSelectionsMax,
  numberOfAllowedSelectionsMin,
  onMaximumSelectionsChange,
  onMinimumSelectionsChange
}: Props) => {
  const { messages } = useReduxState();

  // limit min/max select options so the minimum cannot be higher than maximum,
  // and maximum can't be lower than the minimum
  const selectedMaximumValue =
    typeof numberOfAllowedSelectionsMax === 'number'
      ? Math.min(numberOfAllowedSelectionsMax, maxNumberOfOptions)
      : maxNumberOfOptions;
  const selectedMinimumValue =
    typeof numberOfAllowedSelectionsMin === 'number'
      ? Math.min(numberOfAllowedSelectionsMin, selectedMaximumValue)
      : 0;
  const minimumSelectionOptions = Array.from(
    { length: selectedMaximumValue + 1 },
    (_, index) => ({ label: `${index}`, value: index })
  );
  const maximumSelectionOptions = Array.from(
    {
      length: maxNumberOfOptions - Math.max(selectedMinimumValue, 1) + 1
    },
    (_, index) => {
      const value = index + Math.max(selectedMinimumValue, 1);

      return { label: `${value}`, value };
    }
  );

  const handleMinimumSelectionChange = (value: number) => {
    onMinimumSelectionsChange(value);
  };

  const handleMaximumSelectionChange = (value: number) => {
    onMaximumSelectionsChange(value);
  };

  return (
    <FormGroup
      label={messages.editorBlockMultipleChoiceSelection}
      maxWidth="100%"
    >
      <div className="selection-settings-content">
        <div className="selection-settings-row">
          <div className="select-wrapper">
            <SelectInput
              id={`multiple-choice-${blockId}-selection-min`}
              isDisabled={disabled}
              isSearchable={false}
              onChange={(option: { value: number }) =>
                handleMinimumSelectionChange(option?.value || 0)
              }
              options={minimumSelectionOptions}
              value={
                minimumSelectionOptions.find(
                  (option) => option.value === selectedMinimumValue
                ) || minimumSelectionOptions[0]
              }
            />
          </div>
          <span className="label">
            {messages.editorBlockMultipleChoiceSelectedOptionsMinimum}
          </span>
        </div>

        <div className="selection-settings-row">
          <div className="select-wrapper">
            <SelectInput
              id={`multiple-choice-${blockId}-selection-max`}
              isDisabled={disabled}
              isSearchable={false}
              onChange={(option: { value: number }) =>
                handleMaximumSelectionChange(option?.value || 1)
              }
              options={maximumSelectionOptions}
              value={
                maximumSelectionOptions.find(
                  (option) => option.value === selectedMaximumValue
                ) || maximumSelectionOptions[maximumSelectionOptions.length - 1]
              }
            />
          </div>
          <span className="label">
            {messages.editorBlockMultipleChoiceSelectedOptionsMaximum}
          </span>
        </div>
      </div>

      <style jsx>{`
        .selection-settings-row {
          align-items: center;
          display: flex;
          gap: 10px;
          margin-bottom: 8px;
        }
        .select-wrapper {
          width: 80px;
        }
        .select-wrapper :global(.react-select__control) {
          min-height: auto;
        }
        .select-wrapper :global(.react-select__dropdown-indicator) {
          padding: 4px 6px;
        }
      `}</style>
    </FormGroup>
  );
};

export default EditorBlockChoiceTasksSelectionSettings;
